/* eslint-disable indent */
import Gear from '@expo/vector-icons/FontAwesome';
import Icon from '@expo/vector-icons/MaterialCommunityIcons';
import Feather from '@expo/vector-icons/Feather';
import {
  responsiveFontSize,
  useResponsiveFontSize,
} from '@/components/Util/reactResponsiveDimensions';
import { PressableNeolexon } from '@/components/PressableNeolexon';
import { useCallback, useEffect, useState } from 'react';
import { ScrollView, StyleSheet, View } from 'react-native';
import { Text } from '@/components/Text';
import api from '../../api/axios';
import {
  getIndividualSentenceStats,
  getIndividualWordStats,
  getSentencesetsResultsTimeSeries,
  getWordsetsResultsTimeSeries,
} from '../../api/services/results';
import { localLogoutUser } from '../../api/services/user';
import SelectDropDown from '@/components/SharedComponents/SelectDropDown/SelectDropDown';
import colors from '@/config/colors';
import constants, { isPatientApp } from '@/config/constants';
import { Icon as IconN } from '@/components/Icon';
import { useCommonStyles } from '@/hooks/useCommonStyles';

import { hasInternetAsync } from '../HomeTherapist/helpers';
import ResultDetail from '../ResultDetail';
import { AppHeaderAction } from '@/components/AppHeader/AppHeaderAction';
import RadioButton from '@/components/ui/therapist/RadioButton';

const appModeIsTherapist = constants.appMode === 'therapist';

const dropDownOptions = [
  { label: 'Gesamt', value: '0' },
  { label: '7 Tage', value: '7' },
  { label: '30 Tage', value: '30' },
  { label: '60 Tage', value: '60' },
  { label: '90 Tage', value: '90' },
  { label: '180 Tage', value: '180' },
  { label: '365 Tage', value: '365' },
];

const titleOptions = {
  0: 'Gesamter Zeitraum',
  7: 'Zeitraum 7 Tage',
  30: 'Zeitraum 30 Tage',
  60: 'Zeitraum 60 Tage',
  90: 'Zeitraum 90 Tage',
  180: 'Zeitraum 180 Tage',
  365: 'Zeitraum 365 Tage',
};

const styles = StyleSheet.create({
  center: {
    justifyContent: 'center',
    alignItems: 'center',
  },
  rowSpread: {
    flexDirection: 'row',
  },
  flex1: {
    flex: 1,
  },
  flex2: {
    flex: 2,
  },
  alignItemsCenter: {
    alignItems: 'center',
    alignSelf: 'center',
    textAlign: 'center',
  },
  alignItemsFlexEnd: {
    alignItems: 'flex-end',
    justifyContent: 'center',
  },
  pv10: {
    paddingVertical: 10,
  },
  pr10: {
    paddingRight: 10,
  },
  ph10: {
    paddingHorizontal: 10,
  },
  text1: {
    fontWeight: '700',
    fontSize: responsiveFontSize(1),
    color: colors.darkGrey,
    marginRight: 5,
  },
  text2: {
    fontSize: responsiveFontSize(1),
    color: colors.darkGrey,
  },
  text3: {
    fontSize: responsiveFontSize(1),
    color: colors.darkGrey,
    fontWeight: '500',
  },
  checkBoxWrapper: {
    padding: 0,
    margin: 0,
  },
  bgDarkGrey: {
    backgroundColor: colors.darkerGrey,
  },
  dash: {
    width: 1,
    height: '100%',
    backgroundColor: colors.black,
  },
  textBold: {
    fontWeight: 700,
    fontFamily: 'InterSemiBold',
  },

  noPadding: {
    padding: 0,
  },
  textCenter: {
    textAlign: 'center',
  },
  scrollViewContainer: {
    flexGrow: 1,
  },
});

export const Ergebnisse = (props) => {
  // style
  const commonStyles = useCommonStyles();
  const responsiveFontSize = useResponsiveFontSize(1);
  const { navigation } = props;
  const getResponsiveFontSize = (factor) => {
    return responsiveFontSize * factor;
  };
  let responsiveStyles = StyleSheet.create({
    wiederholen: {
      fontSize: getResponsiveFontSize(2),
      textAlign: 'center',
      color: colors.darkPurpleBlue,
    },
    err1: {
      fontSize: getResponsiveFontSize(2),
      textAlign: 'center',
      color: colors.medium,
    },
    pResponsive: {
      padding: getResponsiveFontSize(1.5),
    },
  });

  const [radioButton, setRadioButton] = useState('therapist');
  const [dropDownSelector, setDropDownSelector] = useState({
    label: 'Gesamt',
    value: '0',
  });
  const [errorMsg, setErrorMsg] = useState({ isErr: true, err1: '', err2: '' });
  const [timeSeriesData, setTimeSeriesData] = useState([]);
  const [individualStatsData, setIndividualStatsData] = useState([]);
  const [webViewTitle, setWebViewTitle] = useState('Gesamter Zeitraum');
  const [handleDownloadLinksClickState, setHandleDownloadLinksClickState] =
    useState(false);

  const changeRadioButton = useCallback((radio) => {
    if (radio === radioButton) {
      return;
    }
    setRadioButton(radio);
  });
  const onSelect = useCallback((newState) => {
    setDropDownSelector((prevState) => {
      if (prevState.value === newState.value) {
        return prevState;
      }
      setWebViewTitle(newState.label);
      return newState;
    });
  });

  const changeHandleDownloadLinksClickState = useCallback((value) => {
    setHandleDownloadLinksClickState(value);
  }, []);

  const getErgenbisseAusDataTherapist = useCallback(
    async (radioButton, dropDown) => {
      setErrorMsg({ err1: '', err2: '', isErr: false });
      try {
        const internet = await hasInternetAsync();
        if (!internet) {
          throw new Error('Network Error');
        }
        const url1 = `api/patients/${props.route?.params?.patientId}/${
          props.route?.params?.exerciseClass
        }s/${props.route?.params?.setId}/individual${
          props.route?.params?.exerciseClass === 'wordset' ? 'word' : 'sentence'
        }stats?app=${radioButton}${
          dropDown.value !== '0' ? `&duration=${dropDown.value}` : ''
        }`;
        const url2 = `api/patients/${props.route?.params?.patientId}/${
          props.route?.params?.exerciseClass
        }s/${props.route?.params?.setId}/resultstimeseries?app=${radioButton}${
          dropDown.value !== '0' ? `&duration=${dropDown.value}` : ''
        }`;

        const res = await Promise.all([api.get(url1), api.get(url2)]);
        setIndividualStatsData(res[0].data);
        setTimeSeriesData(res[1].data);
      } catch (error) {
        if (error.message === 'Network Error') {
          setErrorMsg({
            isErr: true,
            err1: 'Fehler beim Laden der Ergebnisse.',
            err2: 'Bitte überprüfen Sie Ihre Internetverbindung',
          });
        } else if (
          error.response.data.message === 'Patient code is wrong or missing'
        ) {
          setErrorMsg({
            isErr: true,
            err1: 'Zum Ansehen der Ergebnisse aus der App für Patient:innen, muss ein Patienten-Code verbunden sein.',
            err2: '',
          });
        } else {
          setErrorMsg({
            isErr: true,
            err1: (
              <Text>
                Zum Ansehen der Ergebnisse aus dem Eigentraining müssen
                Patient:innen einwilligen. Dazu kann die Option
                <Text style={styles.textBold}>
                  {' '}
                  „Ergebnisse mit dem verbundenen Therapeuten-Account
                  teilen“{' '}
                </Text>
                aktiviert werden, welche über den Menüpunkt
                <Text style={styles.textBold}>
                  <Text>
                    {' '}
                    <Gear
                      name="gear"
                      size={commonStyles.topButtonIconSize.fontSize / 2}
                      color={colors.darkGrey}
                    />
                  </Text>{' '}
                  „Ergebnisse ansehen“
                </Text>{' '}
                in der Aphasie-App aufrufbar ist.
              </Text>
            ),
            err2: '',
          });
        }
      }
    },
    [],
  );

  const dataResults = useCallback(async (dropDown) => {
    setErrorMsg({ err1: '', err2: '', isErr: false });
    try {
      const internet = await hasInternetAsync();
      if (!internet) {
        throw new Error('Network Error');
      }

      let individualStatsResponse = [];
      let timeSeriesResponse = [];
      let durationQuery = '';
      let setId = props.route?.params?.setId;
      if (dropDown.value !== '0') {
        durationQuery = '?duration=' + dropDown.value;
      }
      if (props.route?.params?.exerciseClass === 'wordset') {
        individualStatsResponse = await getIndividualWordStats(
          setId,
          durationQuery,
        );
        timeSeriesResponse = await getWordsetsResultsTimeSeries(
          setId,
          durationQuery,
        );
      } else {
        individualStatsResponse = await getIndividualSentenceStats(
          setId,
          durationQuery,
        );
        timeSeriesResponse = await getSentencesetsResultsTimeSeries(
          setId,
          durationQuery,
        );
      }
      individualStatsResponse.data.find((item) => {
        return item.active;
      });

      setIndividualStatsData(individualStatsResponse.data);
      setTimeSeriesData(timeSeriesResponse.data);
      setWebViewTitle(titleOptions[dropDown.value]);
    } catch (error) {
      if (error.message === 'Network Error') {
        setErrorMsg({
          isErr: true,
          err1: 'Fehler beim Laden der Ergebnisse.',
          err2: 'Bitte überprüfen Sie Ihre Internetverbindung',
        });
      } else {
        await localLogoutUser();
      }
    }
  }, []);

  const refresh = useCallback(() => {
    appModeIsTherapist
      ? getErgenbisseAusDataTherapist(radioButton, dropDownSelector)
      : dataResults(dropDownSelector);
  }, [radioButton, dropDownSelector]);

  useEffect(() => {
    appModeIsTherapist
      ? getErgenbisseAusDataTherapist(radioButton, dropDownSelector)
      : dataResults(dropDownSelector);
  }, [radioButton, dropDownSelector]);

  useEffect(() => {
    navigation.setOptions({
      headerRight: () => (
        <AppHeaderAction
          primary
          onPress={() => {
            changeHandleDownloadLinksClickState(true);
          }}
          icon={<Feather name="download" color="black" />}
          text="herunterladen"
        />
      ),
    });
  }, [navigation]);

  useEffect(() => {
    navigation.setOptions({
      headerLeft: () => (
        <AppHeaderAction
          primary
          onPress={() => navigation.goBack()}
          icon={<IconN name="chevronLeft" />}
          text="Zurück"
        />
      ),
    });
  }, []);

  return (
    <ScrollView contentContainerStyle={styles.scrollViewContainer}>
      {appModeIsTherapist ? (
        <View style={[styles.center, styles.rowSpread, styles.pv10]}>
          <Text style={styles.text1}>Ergebnisse aus</Text>
          <View style={{ marginHorizontal: 8 }}>
            <RadioButton
              label="App Therapeut:innen"
              onPress={() => changeRadioButton('therapist')}
              isSelected={radioButton === 'therapist'}
            />
          </View>
          <View style={{ marginHorizontal: 8 }}>
            <RadioButton
              label="Eigentraining (App Patient:innen)"
              onPress={() => changeRadioButton('patient')}
              isSelected={radioButton === 'patient'}
            />
          </View>
          <View style={{ marginHorizontal: 8 }}>
            <RadioButton
              label="Eigentraining (Klinik-Version)"
              onPress={() => changeRadioButton('therapist-eigentraining')}
              isSelected={radioButton === 'therapist-eigentraining'}
            />
          </View>
        </View>
      ) : null}

      {/* Title Info */}
      <View style={[styles.rowSpread, styles.ph10, styles.bgDarkGrey]}>
        <View
          style={[
            styles.rowSpread,
            styles.alignItemsCenter,
            styles.flex2,
            styles.pv10,
          ]}
        >
          {!isPatientApp && (
            <Text style={[styles.flex1, styles.text3]}>
              Patient:in{' '}
              <Text style={styles.text1}>
                {props.route?.params?.patientName}
              </Text>
            </Text>
          )}
          <Text style={[styles.flex1, styles.text3]}>
            {props.route?.params?.exerciseSet}{' '}
            <Text style={styles.text1}>
              {props.route?.params?.exerciseSetName}
            </Text>
          </Text>
        </View>
        <View style={styles.dash} />
        <View style={[styles.rowSpread, styles.flex2]}>
          <View style={[styles.flex1, styles.alignItemsFlexEnd, styles.pv10]}>
            <Text style={[styles.text1, styles.pr10]}>Zeitraum</Text>
          </View>
          <View style={styles.pv10}>
            <SelectDropDown
              dropDownContentWidth={getResponsiveFontSize(30)}
              fontSize={getResponsiveFontSize(1)}
              onSelect={onSelect}
              options={dropDownOptions}
              defaultValue={dropDownSelector}
              color={colors.darkGrey}
              fontWeight={'700'}
              customTopValue={1.55}
            />
          </View>
        </View>
      </View>

      {/* Body */}
      {errorMsg.isErr ? (
        <View style={[styles.flex1, styles.center]}>
          <View style={responsiveStyles.pResponsive}>
            {errorMsg.err2 ? (
              <>
                <View>
                  <Text
                    style={[
                      responsiveStyles.err1,
                      { fontWeight: '700', color: colors.black },
                    ]}
                  >
                    {errorMsg.err1}
                  </Text>
                </View>
                <Text style={styles.textCenter}>{errorMsg.err2}</Text>
              </>
            ) : (
              <Text style={responsiveStyles.err1}>{errorMsg.err1}</Text>
            )}
          </View>
          <PressableNeolexon
            onPress={refresh}
            style={[styles.center, styles.rowSpread]}
          >
            <Icon
              name="reload"
              color={colors.darkPurpleBlue}
              size={commonStyles.topButtonIconSize.fontSize}
            />
            <Text style={responsiveStyles.wiederholen}>wiederholen</Text>
          </PressableNeolexon>
        </View>
      ) : (
        <ResultDetail
          navigation={navigation}
          route={props.route}
          appModeIsTherapist={appModeIsTherapist}
          handleDownloadLinksClickState={handleDownloadLinksClickState}
          changeHandleDownloadLinksClickState={
            changeHandleDownloadLinksClickState
          }
          duration={dropDownSelector.value}
          timeSeriesData={timeSeriesData}
          individualStatsData={individualStatsData}
          webViewTitle={webViewTitle}
        />
      )}
    </ScrollView>
  );
};
